import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Images, SealCheck, MapPin, Copy, Planet, Leaf, Buildings, GlobeHemisphereEast,
  WarningCircle, Eye, Broadcast, ArrowRight, GpsFix, Robot, MapPinLine,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { Card } from "@/components/ui/card";

const STAT_CARDS = [
  { key: "total", label: "Total Photos", icon: Images, color: "#F27A54" },
  { key: "location_identified", label: "Location Identified", icon: MapPin, color: "#10B981" },
  { key: "needs_review", label: "Needs Review", icon: SealCheck, color: "#FFC107", link: "/review" },
  { key: "nature_subjects", label: "Nature Subjects", icon: Leaf, color: "#16A34A" },
  { key: "landmarks", label: "Landmarks", icon: GlobeHemisphereEast, color: "#7C3AED" },
  { key: "published", label: "Published", icon: Broadcast, color: "#F27A54" },
  { key: "duplicates", label: "Duplicates", icon: Copy, color: "#EF4444" },
  { key: "private", label: "Private", icon: Eye, color: "#52525B" },
];

const PIPELINE = [
  { key: "analyzing", label: "Analyzing" },
  { key: "analyzed", label: "Analyzed" },
  { key: "needs_review", label: "Needs Review" },
  { key: "approved", label: "Approved" },
  { key: "published", label: "Published" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const domainData = stats ? Object.entries(stats.domains || {}).map(([name, count]) => ({ name, count })) : [];
  const domainColors = ["#F27A54", "#10B981", "#FFC107", "#7C3AED", "#EF4444", "#16A34A", "#0EA5E9", "#F97316", "#64748B"];

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline text-[#F27A54]">Photo Intelligence Control Center</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Dashboard</h1>
        </div>
        <Link to="/upload" data-testid="dashboard-import-btn"
          className="flex items-center gap-2 rounded-md bg-[#F27A54] px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#d9633f]">
          Import Photos <ArrowRight size={16} weight="bold" />
        </Link>
      </div>

      {/* Stat grid */}
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        {STAT_CARDS.map((c) => {
          const inner = (
            <Card className="flex h-full flex-col justify-between rounded-md border border-border p-5 shadow-none transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm">
              <div className="flex items-center justify-between">
                <c.icon size={20} weight="bold" style={{ color: c.color }} />
                {c.link && <ArrowRight size={14} className="text-muted-foreground" />}
              </div>
              <div className="mt-6">
                <p data-testid={`stat-${c.key}`} className="font-display text-3xl font-black tracking-tight">
                  {stats ? (stats[c.key] ?? 0).toLocaleString() : "—"}
                </p>
                <p className="overline mt-1 text-muted-foreground">{c.label}</p>
              </div>
            </Card>
          );
          return c.link ? <Link key={c.key} to={c.link}>{inner}</Link> : <div key={c.key}>{inner}</div>;
        })}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* Domain distribution */}
        <Card className="rounded-md border border-border p-5 shadow-none lg:col-span-2">
          <p className="overline text-muted-foreground">Domain Distribution</p>
          <h3 className="font-display text-lg font-bold">Where knowledge is flowing</h3>
          <div className="mt-4 h-64 min-h-[16rem] w-full">
            {domainData.length ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={domainData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f4f4f5" }} contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid #e4e4e7" }} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {domainData.map((_, i) => <Cell key={i} fill={domainColors[i % domainColors.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="grid h-full place-items-center text-sm text-muted-foreground">
                No analyzed photos yet. Import photos to populate.
              </div>
            )}
          </div>
        </Card>

        {/* Pipeline */}
        <Card className="rounded-md border border-border p-5 shadow-none">
          <p className="overline text-muted-foreground">Publishing Pipeline</p>
          <h3 className="font-display text-lg font-bold">Workflow status</h3>
          <div className="mt-4 space-y-3">
            {PIPELINE.map((s) => {
              const val = stats ? stats[s.key] ?? 0 : 0;
              const total = stats?.total || 1;
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-zinc-700">{s.label}</span>
                    <span className="font-mono-p text-zinc-500">{val}</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-sm bg-zinc-100">
                    <div className="h-full rounded-sm bg-[#F27A54] transition-all duration-500"
                      style={{ width: `${Math.min((val / total) * 100, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Location intelligence: GPS vs AI-inferred */}
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <div className="flex items-center gap-2">
          <MapPin size={18} weight="fill" className="text-[#F27A54]" />
          <h3 className="font-display text-lg font-bold">Location Intelligence</h3>
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">How each photo was geolocated — GPS EXIF vs AI visual inference.</p>
        <div className="mt-4 grid grid-cols-3 gap-4">
          {[
            { key: "gps_located", label: "GPS-tagged", icon: GpsFix, color: "#10B981", sub: "from EXIF coordinates" },
            { key: "ai_located", label: "AI-located", icon: Robot, color: "#7C3AED", sub: "no GPS · visual inference" },
            { key: "no_location", label: "No location", icon: MapPinLine, color: "#64748B", sub: "needs manual location" },
          ].map((c) => (
            <div key={c.key} className="rounded-md border border-border p-4">
              <c.icon size={18} weight="bold" style={{ color: c.color }} />
              <p data-testid={`stat-${c.key}`} className="mt-3 font-display text-2xl font-black">{stats ? (stats[c.key] ?? 0) : "—"}</p>
              <p className="overline mt-0.5 text-muted-foreground">{c.label}</p>
              <p className="mt-1 text-[11px] text-zinc-400">{c.sub}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Countries */}
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <div className="flex items-center gap-2">
          <Planet size={18} weight="bold" className="text-[#F27A54]" />
          <h3 className="font-display text-lg font-bold">Geographic Coverage</h3>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {stats && Object.keys(stats.countries || {}).length ? (
            Object.entries(stats.countries).map(([country, count]) => (
              <div key={country} className="flex items-center gap-2 rounded-sm border border-border px-3 py-1.5 text-sm">
                <span className="font-medium">{country}</span>
                <span className="font-mono-p text-xs text-muted-foreground">{count}</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No geolocated photos yet.</p>
          )}
        </div>
      </Card>
    </div>
  );
}
